import { FunctionComponent, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { ReceiptLongOutlined } from '@mui/icons-material';
import { DialogWrapper } from '../../../presentation-components/display-elements-components/dialog-wrapper';
import FileUpload from '../../../presentation-components/miscellaneous-components/file-upload';
import FileUploadService from '../../../../shared/services/file-upload/file-upload.service';
import { BillType } from '../../../../shared/services/file-upload/file-upload.model';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';

// Bill upload for a single outstanding action: just the file picker, because everything else is
// already known from the action (its report period, and the bill kind from the action's group).
// Submitting mirrors the import wizard's bill path exactly: the same upload call, then the same
// handoff to the upload-progress page, which returns into the wizard's review step. The rules
// template is implied: bills always resolve their template server side.
export const UploadBillDialog: FunctionComponent<{
  open: boolean;
  closeHandler: () => void;
  reportPeriodId: string;
  billType: BillType;
  // The outstanding action this upload came from; the worker completes it once the
  // upload's rows commit.
  reportActionId?: string;
}> = ({ open, closeHandler, reportPeriodId, billType, reportActionId }) => {
  const navigate = useNavigate();
  const userNotificationsContext = useContext(UserNotificationsContext);
  const [files, setFiles] = useState<File[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // With `multiple`, react-drag-drop-files hands back a FileList rather than a File, and bills
  // append across drops (a year of bills is often dragged in a few batches).
  const storeFiles = (file: any) => {
    setUploadError(null);
    const incoming: File[] = file instanceof FileList ? Array.from(file) : [file];
    setFiles((existing) => [...existing, ...incoming]);
  };

  const removeFile = (index: number) => {
    setFiles((existing) => existing.filter((_, i) => i !== index));
  };

  const handleClose = () => {
    if (isUploading) return;
    setFiles([]);
    setUploadError(null);
    closeHandler();
  };

  const submitHandler = async () => {
    if (files.length === 0 || !reportPeriodId) return;
    setIsUploading(true);
    try {
      const form = new FormData();
      files.forEach((file) => form.append('files', file));
      form.append('billType', billType);
      if (reportActionId) {
        form.append('reportActionId', reportActionId);
      }
      const fileUploadService = new FileUploadService();
      const response = await fileUploadService.uploadBills(form, `?reportPeriodId=${reportPeriodId}`);
      if (response?.id) {
        // Same handoff as the wizard's bill path: the progress page polls the extraction,
        // then returns into the import flow's review step.
        navigate(`/settings/data-upload/import-data/${response.id}`);
      }
    } catch (error: any) {
      console.log(error);
      if (!error.response || error?.response?.status === 504) {
        navigate('/time-out?type=import');
        return;
      }
      userNotificationsContext.openDialog({
        active: true,
        title: 'Upload Failed',
        message: error?.response?.data?.message || 'There was an error, please try again',
        buttonCopy: 'Ok',
        hideCancelButton: true,
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <DialogWrapper
      open={open}
      onClose={handleClose}
      title={billType === 'fuel' ? 'Upload fuel bills' : 'Upload energy bills'}
      cancelHandler={handleClose}
      submitHandler={submitHandler}
      isSubmitting={isUploading}
      disableSaveButton={files.length === 0}
      maxWidth='sm'
      submitButtonText='Upload'
      submitButtonLoadingText='Uploading...'
    >
      <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2 }}>
        {billType === 'fuel'
          ? 'Add fuel bills or fuel card statements for this action. We read the fuel, quantities and dates off each document, and you check everything we found before anything is imported.'
          : 'Add energy bills for this action. We read the consumption, dates and meter details off each bill, and you check everything we found before anything is imported.'}
      </Typography>
      {uploadError && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {uploadError}
        </Alert>
      )}
      <FileUpload
        document={true}
        multiple={true}
        // Exactly the formats the bill reader accepts, matching the import wizard.
        allowedFileTypes={['PDF', 'PNG', 'JPG', 'JPEG', 'TIF', 'TIFF']}
        handleFileUpload={storeFiles}
        onError={(msg: string) => setUploadError(msg)}
        typeErrorMessage='Unsupported file type. Please upload a PDF or an image of your bill.'
        maxSizeMb={15}
      />
      {files.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant='subtitle2' sx={{ mb: 1 }}>
            {files.length} {files.length === 1 ? 'bill' : 'bills'} ready to upload
          </Typography>
          <Stack spacing={0.5}>
            {files.map((file, index) => (
              <Box
                key={`${file.name}-${index}`}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  border: 1, borderColor: 'divider', borderRadius: 1, px: 1.5, py: 0.75,
                }}
              >
                <Stack direction='row' spacing={1} alignItems='center' sx={{ minWidth: 0 }}>
                  <ReceiptLongOutlined fontSize='small' sx={{ color: 'text.secondary' }} />
                  <Typography variant='body2' noWrap title={file.name}>{file.name}</Typography>
                </Stack>
                <Button
                  size='small'
                  variant='outlined'
                  onClick={() => removeFile(index)}
                  sx={{
                    color: 'text.secondary',
                    borderColor: 'lightGrey.main',
                    '&:hover': { color: 'text.primary', borderColor: 'text.secondary', bgcolor: 'action.hover' },
                  }}
                >
                  <Typography variant='body2'>Remove</Typography>
                </Button>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </DialogWrapper>
  );
};
